import localApiAdapter from '../../core/services/localApiAdapter';
import { isAchatsCatalogLowStock } from './achatsListPageUtils';

/** Normalize list payloads from localApiAdapter ({ data } or bare array). */
export function unwrapAchatsApiList(response) {
  const data = response?.data ?? response;
  return Array.isArray(data) ? data : [];
}

export async function fetchAchatsSuppliers() {
  try {
    const response = await localApiAdapter.get('/facturation/fournisseurs');
    return unwrapAchatsApiList(response);
  } catch {
    return [];
  }
}

/** Articles / products catalog (shared ERP products). */
export async function fetchAchatsProducts() {
  try {
    const response = await localApiAdapter.get('/products');
    return unwrapAchatsApiList(response);
  } catch {
    return [];
  }
}

/** Supplier catalog entries (achats module). */
export async function fetchAchatsCatalog() {
  try {
    const response = await localApiAdapter.get('/achats/catalogue-fournisseurs');
    return unwrapAchatsApiList(response).map(normalizeAchatsCatalogItem);
  } catch {
    return [];
  }
}

/** Inventory warehouses for goods receipt routing. */
export async function fetchAchatsWarehouses() {
  try {
    const response = await localApiAdapter.get('/inventory/warehouses');
    return unwrapAchatsApiList(response);
  } catch {
    return [];
  }
}

export function normalizeAchatsCatalogItem(item) {
  return {
    ...item,
    product_code: item.product_code || item.supplier_sku || item.catalog_code || '',
  };
}

/** Match a catalog row by product_id, code, or name. */
export function findAchatsCatalogItem(catalogItems, { productId, productCode, productName } = {}) {
  if (!Array.isArray(catalogItems) || !catalogItems.length) return null;

  if (productId) {
    const byId = catalogItems.find((c) => String(c.product_id) === String(productId));
    if (byId) return byId;
  }

  const code = String(productCode || '').trim().toLowerCase();
  if (code) {
    const byCode = catalogItems.find((c) => {
      const cCode = String(c.product_code || c.supplier_sku || c.catalog_code || '').trim().toLowerCase();
      return cCode && cCode === code;
    });
    if (byCode) return byCode;
  }

  const name = String(productName || '').trim().toLowerCase();
  if (name) {
    return catalogItems.find((c) => String(c.product_name || '').trim().toLowerCase() === name) || null;
  }

  return null;
}

export function countAchatsLowStockCatalog(catalogItems) {
  return (catalogItems || []).filter(isAchatsCatalogLowStock).length;
}

export function buildAchatsProductSelectOptions(products, catalogItems, { lowStockAlertEnabled = false } = {}) {
  return (products || []).map((product) => {
    const catalogItem = findAchatsCatalogItem(catalogItems, {
      productId: product.id,
      productCode: product.code || product.sku,
      productName: product.name,
    });
    const lowStock = lowStockAlertEnabled && catalogItem && isAchatsCatalogLowStock(catalogItem);
    const code = product.code || product.sku;
    return {
      value: product.id,
      label: `${product.name || product.id}${code ? ` (${code})` : ''}${lowStock ? ' ⚠' : ''}`,
      product,
      catalogItem,
      lowStock,
    };
  });
}

/** Map articles product + optional supplier-catalog row into achats form fields. */
export function mapAchatsProductSelection(product, catalogItems, { lowStockAlertEnabled = false } = {}) {
  const catalogItem = findAchatsCatalogItem(catalogItems, {
    productId: product?.id,
    productCode: product?.code || product?.sku,
    productName: product?.name,
  });

  const patch = {
    product_id: product?.id?.toString() || '',
    product_name: product?.name || '',
    product_code: product?.code || product?.sku || catalogItem?.product_code || '',
    unit_price: product?.price?.toString() || catalogItem?.unit_price?.toString() || '',
  };

  if (lowStockAlertEnabled && catalogItem && isAchatsCatalogLowStock(catalogItem)) {
    patch.urgency = 'high';
    if (catalogItem.supplier_name) {
      patch.supplier_suggestion = catalogItem.supplier_name;
    }
  }

  return patch;
}
